import { useRouter } from 'next/router';
import Script from 'next/script';
import { ReactNode, useEffect, useRef } from 'react';
import { twMerge } from 'tailwind-merge';

import Modal from '../Modal';

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
  }
}

interface DataLayerData {
  userId?: string;
  orderId?: string;
}

type Props = {
  className?: string;
  modalClassName?: string;
  onClose?: () => void;
  ariaLabel?: string;
  children?: ReactNode;
  dataLayerData?: DataLayerData;
  targetOrigin?: string;
  onLoaded?: () => void;
};

const SRC_NL = 'https://techonomy.campaign.playable.com/boss-paint-rad?onlyflow=1&iframe=dontbreakout';
const SRC_FR = 'https://techonomy.campaign.playable.com/fr-boss-paints-rad?onlyflow=1&iframe=dontbreakout';

/*
 * Modal component specifically designed for displaying iframes.
 * Extends the base Modal component with iframe-specific functionality.
 */
const SpinTheWheelModal = ({
  className,
  modalClassName,
  onClose,
  children,
  dataLayerData,
  targetOrigin = '*',
  onLoaded,
}: Props) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const { locale } = useRouter();
  const source = locale === 'fr' ? SRC_FR : SRC_NL;

  useEffect(() => {
    if (!dataLayerData || typeof window === 'undefined') {
      return;
    }

    // Push to parent dataLayer
    window.addEventListener('message', e => {
      try {
        const data = JSON.parse(e.data);
        const source = e.source;

        if (source && data.type === 'leadfamly-campaign' && data.action === 'user-recognition-ready') {
          (source as Window).postMessage(
            JSON.stringify({
              type: 'leadfamly-campaign',
              action: 'user-recognition-tokens',
              data: dataLayerData,
            }),
            targetOrigin,
          );
        }
      } catch (e) {
        console.error(e);
      }
    });
  }, [dataLayerData, targetOrigin]);

  // Send data to iframe when it loads
  const handleIframeLoad = () => {
    if (onLoaded && dataLayerData && typeof window !== 'undefined') {
      onLoaded();
    }
  };

  return (
    <Modal
      ariaLabel="Spin the wheel marketing modal"
      className={twMerge(
        // Override default max-width for iframe content
        'max-h-9/10 left-0 right-0 max-w-4xl p-2 md:left-5 md:right-5',
        modalClassName,
      )}
      onClose={onClose}
    >
      {children && <div className="mb-4">{children}</div>}
      <div className={twMerge('w-full', className)}>
        <Script src="https://app.playable.com/campaign/sdk/utilities.responsive_iframe.min.js" />

        <iframe
          allow="autoplay; fullscreen"
          onLoad={handleIframeLoad}
          ref={iframeRef}
          src={source}
          style={{ border: 'none', width: '100%', height: '500px', maxHeight: '1000px' }}
          title="BOSS paint - Rad"
        />
      </div>
    </Modal>
  );
};

export default SpinTheWheelModal;
